import React, { useState } from "react";
import { Mail, Lock, User, Eye, EyeOff, CheckCircle, Phone } from "lucide-react";
import { Link } from "react-router-dom";

function SignupPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone: ''
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const err = {};
    if (!formData.name.trim()) err.name = "Full name is required";
    if (!formData.email) err.email = "Email is required";
    if (!formData.phone) err.phone = "mobile number is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      err.email = "Invalid email address";
    if (!formData.password) err.password = "Password is required";
    else if (formData.password.length < 6)
      err.password = "Minimum 6 characters required";
    return err;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const err = validateForm();
    if (Object.keys(err).length) {
      setErrors(err);
      return;
    }

    // DEBUG: Log what we're sending
    console.log('📤 Sending signup data:', {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      passwordLength: formData.password.length
    });

    setLoading(true);
    setErrors({});

    try {
      const response = await fetch('https://api.boomontrip.com/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      console.log('📥 Signup response:', data);

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Signup failed');
      }

      // FIXED: Use consistent localStorage keys (authToken and userData)
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('userData', JSON.stringify(data.user));

      setSuccess(true);

      setTimeout(() => {
        window.location.href = '/';
      }, 1500);

    } catch (error) {
      console.error('❌ Signup error:', error);
      setErrors({ general: error.message || 'Signup failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Success Screen
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="text-green-600" size={48} />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Account Created! 🎉
          </h2>
          <p className="text-gray-600 mb-6">
            Redirecting to homepage...
          </p>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-green-600 h-2 rounded-full animate-progress"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-xl p-8">
        <h2 className="text-3xl font-bold text-center mb-2">
          Create Account
        </h2>
        <p className="text-center text-gray-600 text-sm mb-6">
          Join BoomOnTrip and start exploring
        </p>

        {errors.general && (
          <div className="mb-4 text-sm bg-red-50 text-red-600 px-4 py-3 rounded-lg">
            {errors.general}
          </div>
        )}

        <form className="space-y-5" onSubmit={handleSubmit}>
          {/* NAME */}
          <div>
            <label className="text-sm font-medium">Full Name</label>
            <div className="relative mt-1">
              <User
                className="absolute left-3 top-3 text-gray-400"
                size={18}
              />
              <input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter full name"
                className={`w-full pl-10 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 outline-none ${errors.name ? "border-red-400" : "border-gray-300"
                  }`}
              />
            </div>
            {errors.name && (
              <p className="text-xs text-red-500 mt-1">{errors.name}</p>
            )}
          </div>

          {/* EMAIL */}
          <div>
            <label className="text-sm font-medium">Email Address</label>
            <div className="relative mt-1">
              <Mail
                className="absolute left-3 top-3 text-gray-400"
                size={18}
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                className={`w-full pl-10 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 outline-none ${errors.email ? "border-red-400" : "border-gray-300"
                  }`}
              />
            </div>
            {errors.email && (
              <p className="text-xs text-red-500 mt-1">{errors.email}</p>
            )}
          </div>

          {/* PHONE */}
          <div>
            <label className="text-sm font-medium">Phone</label>
            <div className="relative mt-1">
              <Phone
                className="absolute left-3 top-3 text-gray-400"
                size={18}
              />
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="9876543210"
                className={`w-full pl-10 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 outline-none ${errors.phone ? "border-red-400" : "border-gray-300"
                  }`}
              />
            </div>
            {errors.phone && (
              <p className="text-xs text-red-500 mt-1">{errors.phone}</p>
            )}
          </div>

          {/* PASSWORD */}
          <div>
            <label className="text-sm font-medium">Password</label>
            <div className="relative mt-1">
              <Lock
                className="absolute left-3 top-3 text-gray-400"
                size={18}
              />
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Create password (min 6 characters)"
                className={`w-full pl-10 pr-10 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 outline-none ${errors.password ? "border-red-400" : "border-gray-300"
                  }`}
              />
              <span
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3 text-gray-400 cursor-pointer"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </span>
            </div>
            {errors.password && (
              <p className="text-xs text-red-500 mt-1">{errors.password}</p>
            )}
          </div>

          {/* SUBMIT BUTTON */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-md font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Creating Account..." : "Create Account"}
          </button>

          <p className="text-center text-sm mt-4">
            Already have an account?{" "}
            <Link to="/login" className="text-blue-600 font-medium hover:underline">
              Login
            </Link>
          </p>
        </form>
      </div>

      <style>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
        .animate-progress {
          animation: progress 1.5s ease-out;
        }
      `}</style>
    </div>
  );
}

export default SignupPage;